import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Iter 136 · Google Sign-In callback handler.
 *
 * 1. Reads `session_id` from the URL fragment.
 * 2. Immediately strips the fragment so the opaque id never sticks around.
 * 3. POSTs it to the backend which exchanges it for our own HttpOnly cookies.
 * 4. Redirects to /pureh/dashboard on success, or /pureh/login?err=... otherwise.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const done = useRef(false);
  const [status, setStatus] = useState<string>("Verifying your Google sign-in…");

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const raw = window.location.hash || "";
    const params = new URLSearchParams(raw.replace(/^#/, ""));
    const sid = params.get("session_id") || "";
    // Strip fragment ASAP so session_id doesn't linger in history.
    if (window.history?.replaceState) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (!sid) {
      setStatus("No Google session was returned. Please try again.");
      setTimeout(() => nav("/pureh/login?err=nosession", { replace: true }), 1200);
      return;
    }
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${base}/api/pureh/auth/google/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sid }),
        });
        if (r.status === 200) {
          setStatus("Signed in. Redirecting…");
          nav("/pureh/dashboard", { replace: true });
          return;
        }
        let detail = "Access denied.";
        try { const j = await r.json(); detail = j.detail || detail; } catch { /* noop */ }
        setStatus(detail);
        setTimeout(() => nav(`/pureh/login?err=${encodeURIComponent(detail)}`, { replace: true }), 1500);
      } catch {
        setStatus("Unable to reach the sign-in service. Please retry.");
        setTimeout(() => nav("/pureh/login?err=network", { replace: true }), 1500);
      }
    })();
  }, [nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="auth-callback-page">
      <div className="text-sm text-slate-700" data-testid="auth-callback-status">{status}</div>
    </div>
  );
}
